import api from "./api";

// ---------- Types ----------
export interface CurrentSubscriptionData {
  id: string;
  subscriptionPlanId: string;
  planName: string;
  status: string;               // e.g. "Active"
  totalListings: number;
  usedListings: number;
  price: number;
  daysLeft: number;
  featuredUsed?: number;
  featuredTotal?: number;
  startDate?: string;
  endDate?: string;
  validityType?: string;  
  validityValue?: number;
  source?: 'admin' | 'payzone'; 
}

export interface BillingRecord {
  date: string;                 // formatted, e.g. "Jun 1, 2024"
  amount: number;
  plan: string;
  status: string;               // "paid", "pending", "failed"
  invoiceNumber?: string;
}

export interface BillingResponse {
  data: BillingRecord[];
  pagination: {
    total: number;
    limit: number;
    offset: number;
  };
}

export interface SubscriptionPlan {
  id: string;
  SubscriptionName: string;
  SubscriptionNameML?: any;
  Description?: string;
  DescriptionML?: any;
  CustomerType: number;
  NumberOfListings: number;
  ValidityType: string;
  ValidityValue: number;
  Price: number;
  IsTrial: boolean;
  IsActive: boolean;
  IsPopular?: boolean; // for UI marking
}
// ---------- Service Functions ----------

/**
 * Get the current user's active subscription with usage details
 * @returns CurrentSubscriptionData or null if no active subscription
 */
export const getCurrentSubscription = async (): Promise<CurrentSubscriptionData | null> => {
  const response = await api.get("/csubscription/current");
  return response.data.data;   // returns null if no subscription
};

export const getSubscriptionPlans = async (customerType?: number): Promise<SubscriptionPlan[]> => {
  const params: any = {};
  if (customerType) params.customerType = customerType;
  const response = await api.get("/csubscription/plans", { params });
  return response.data.data || [];
};

export const getAllSubscriptionPlans = async (customerType?: number): Promise<SubscriptionPlan[]> => {
  const params: any = {};
  if (customerType) params.customerType = customerType;
  const response = await api.get("/csubscription/allplans", { params });
  return response.data.data || [];
};
/**
 * Upgrade to a new subscription plan
 * @param subscriptionPlanId - ID of the plan to upgrade to (from your plans list)
 * @returns { success, message, data } with the new subscription details
 */
// ---------- Types ----------
export interface PaywallData {
  paymentAttemptId: string;
  orderId: string;
  chargeId: string;
  paywallUrl: string;
  payloadJson: string;
  signature: string;
}

export interface PaywallResponse {
  success: boolean;
  message: string;
  data: PaywallData;
}

export interface PaymentStatusData {
  status: 'PENDING' | 'PAID' | 'FAILED' | 'CANCELLED';
  failureReason: string | null;
  subscriptionId: string | null;
}

// ---------- Payment session ----------
export const purchaseSubscription = async (
  subscriptionPlanId: string
): Promise<PaywallResponse> => {
  // customerId is no longer sent — backend uses JWT.
  const response = await api.post('/csubscription/purchase', { subscriptionPlanId });
  return response.data;
};

export const upgradeSubscription = async (
  subscriptionPlanId: string
): Promise<PaywallResponse> => {
  const response = await api.put('/csubscription/upgrade', { subscriptionPlanId });
  return response.data;
};

export const renewSubscription = async (): Promise<PaywallResponse> => {
  const response = await api.put('/csubscription/renew');
  return response.data;
};

export const getPaymentStatus = async (
  orderId: string
): Promise<{ success: boolean; data: PaymentStatusData }> => {
  const response = await api.get(`/csubscription/payment/status/${orderId}`);
  return response.data;
};

/**
 * Get billing history for the logged-in user
 * @param params - optional pagination (limit, offset)
 * @returns { data: BillingRecord[], pagination: { total, limit, offset } }
 */
export const getBillingHistory = async (params?: { limit?: number; offset?: number }) => {
  const response = await api.get("/csubscription/billing", { params });
  return response.data;   // { data, pagination }
};
// =====================
// EXTENSION HISTORY (CUSTOMER)
// =====================

export interface ExtensionHistoryRecord {
  id: string;
  operationType: 'EXTEND' | 'NEW';
  subscriptionName: string;
  previousEndDate: string;
  newEndDate: string;
  monthsExtended: number;
  notes: string;
  createdOn: string;
}

export interface ExtensionHistoryResponse {
  success: boolean;
  total: number;
  data: ExtensionHistoryRecord[];
}

export const getMyExtensionHistory = async (): Promise<ExtensionHistoryResponse> => {
  const response = await api.get('/csubscription/extensions');
  return response.data;
};
export interface ListingUsagePricingOption {
  duration: 'Week' | 'Month' | 'Year';
  requestedDays: number;
  servedDays: number;
  unitPriceFull: number;
  unitPriceActual: number;
  prorationFactor: number;
  willBeProrated: boolean;
  expiresOn: string;
}

export interface ListingUsage {
  baseSlots: number;
  extraSlots: number;
  totalAllowed: number;
  usedSlots: number;
  remainingSlots: number;
  isUnlimited: boolean;
  daysRemainingInParent: number;
  isFirstDayOfPlan: boolean;
  parentPlanValidityType: string;
  availableOptions: ListingUsagePricingOption[];
  activePurchases: Array<{
    id: string;
    quantity: number;
    duration: string;
    startsOn: string;
    expiresOn: string;
  }>;
}

export const getListingUsage = async (): Promise<{
  success: boolean;
  data: ListingUsage | null;
}> => {
  const response = await api.get('/csubscription/listing-usage');
  return response.data;
};

export const initiateAdditionalListingPurchase = async (payload: {
  quantity: number;
  duration: 'Week' | 'Month' | 'Year';
}) => {
  const response = await api.post(
    '/csubscription/additional-listings/purchase',
    payload
  );
  return response.data;
};

export const getMyAdditionalListingPurchases = async () => {
  const response = await api.get('/csubscription/additional-listings');
  return response.data;
};